import { useState, useEffect } from 'react';
import { coreAPI } from '../../lib/api';
import type { UserProfile } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

interface UserPlatformStaffFieldProps {
  /** The user whose platform-staff flag is shown. */
  selectedUser: UserProfile;
}

/**
 * Platform-staff toggle in the User Details modal.
 *
 * Super admins only — this is the operator bypass, not a company admin marker,
 * so no one else sees or sets it. The backend refuses anyone else and
 * serializes is_platform_staff as null for them, so a customer admin never
 * even learns the flag's state. Offered on admin-role users (who are the ones
 * that need it) and on anyone already holding it, so it can be reviewed and
 * revoked from the same place. Optimistic local copy so the switch reflects
 * the change without refetching the whole list.
 *
 * Extracted from UserDetailsModal to keep that file within the 500-line limit.
 */
export function UserPlatformStaffField({ selectedUser }: UserPlatformStaffFieldProps) {
  const { user: viewer } = useAuth();
  const viewerIsSuperuser = Boolean(viewer?.is_superuser);
  const [staffFlag, setStaffFlag] = useState<boolean | null>(null);
  const [savingStaffFlag, setSavingStaffFlag] = useState(false);
  // Keyed on id + flag, not the object: a patched row (e.g. a department
  // save) must not snap a just-toggled switch back to the stale prop.
  useEffect(() => {
    setStaffFlag(selectedUser.is_platform_staff ?? null);
  }, [selectedUser.id, selectedUser.is_platform_staff]);

  const togglePlatformStaff = async (next: boolean) => {
    setSavingStaffFlag(true);
    try {
      const res = await coreAPI.setPlatformStaff(selectedUser.id, next);
      setStaffFlag(res.data.is_staff);
      toast.success(res.data.message);
    } catch (err: unknown) {
      console.error('Failed to change platform staff access:', err);
      const detail = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(detail || 'Failed to change platform staff access');
    } finally {
      setSavingStaffFlag(false);
    }
  };

  if (!viewerIsSuperuser
    || selectedUser.company_type === 'helix'
    || !(selectedUser.role_details?.is_admin_role || staffFlag)) {
    return null;
  }

  return (
    <div>
      <div className="text-sm font-medium text-muted-foreground">Platform Staff</div>
      <div className="mt-1 flex items-center gap-3">
        <Switch
          checked={Boolean(staffFlag)}
          disabled={savingStaffFlag}
          onCheckedChange={togglePlatformStaff}
          aria-label="Platform staff access"
        />
        <span className="text-sm">
          {savingStaffFlag
            ? 'Saving…'
            : staffFlag ? 'Has platform staff access' : 'No platform staff access'}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        Grants operator-level access across tenants — platform settings, email queue
        and temporary passwords. Give it only to people who run the platform.
      </p>
    </div>
  );
}

export default UserPlatformStaffField;
